// Angular Core
import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { FormBuilder, Validators, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Router } from '@angular/router';
import { Location } from '@angular/common';

// RxJS
import { catchError, filter, finalize, of, tap } from 'rxjs';

// Services
import { PaymentMethodService } from '../../settings/billing/payment-method/payment-method.service';
import { AccountService } from '../../shared/account.service';
import { AddressService } from '../../shared/address.service';
import { KlaviyoService } from '../../shared/klaviyo/klaviyo.service';
import { FirebaseCrudService } from '../../shared/firebase-crud.service';
import { LoadingService } from '../../shared/loading.service';
import { ModalContentService } from '../../shared/modal-content/modal-content.service';
import { LocalStorageService } from '../../shared/local-storage.service';
import { ProductsService } from '../../product/products.service';
import { ApiService } from '../../shared/api.service';
import { SubscriptionsService } from '../../settings/account/subscriptions/subscriptions.service';
import { DeliveriesService } from '../../settings/account/deliveries/deliveries.service';

// Types
import { DeliveryOptionsResponseItem, NewAccountPayload, Reference } from '../../shared/types/account.types';
import { Product } from '../../product/product.types';
import { ApiResponse } from '../../shared/common/types';
import { Session } from '../../shared/types/session.type';
import { Frequencies } from '../../shared/types/common.types';
import { RequestHandlerParams } from '../../shared/types/api-service.types';
import { SubscriptionPayload } from '../../settings/account/subscriptions/subscriptions.types';

// Constants
import { FIREBASE_COLLECTIONS, LOCALSTORAGE_KEYS } from '../../shared/constants/databases';
import { ModalContentTypes } from '../../shared/constants/modal-content-types';
import { DataLayerService } from '../../shared/data-layer/data-layer.service';
import { formatDateToReadableString } from '../../shared/utils/formatting';
import { KLAVIYOEVENTS } from '../../shared/klaviyo/events';
import { SignalsStoreService } from '../../shared/signals-store.service';
import { DataLayer_SignUpFunnel_User } from '../../shared/data-layer/data-layer.types';

export interface SignupFlowStep { id: number; key: string; title: string; completed?: boolean; last?: boolean; }

/**
 * Password strength validator that enforces:
 * - Minimum 8 characters
 * - At least one uppercase letter
 * - At least one lowercase letter
 * - At least one number
 * - At least one special character
 * - No whitespace characters
 */
export const passwordStrengthValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value = control.value as string;
  if (!value) return null; // Required validation is handled by another validator

  const errors: any = {};
  if (value.length < 8) errors.minLength = true;
  if (!/[A-Z]/.test(value)) errors.requiredUpper = true;
  if (!/[a-z]/.test(value)) errors.requiredLower = true;
  if (!/[0-9]/.test(value)) errors.requiredNumber = true;
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value)) errors.requiredSpecial = true;
  if (/\s/.test(value)) errors.noWhitespace = true;

  return Object.keys(errors).length ? errors : null;
};

/**
 * Phone number length validator that enforces:
 * - Minimum 10 digits
 * - Maximum 15 digits
 * - Only numeric characters
 */
export const phoneLengthValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value = control.value as string;
  if (!value) return null; // Required validation is handled by another validator

  const numericValue = value.replace(/\D/g, ''); // Remove non-numeric characters

  if (numericValue.length < 10) return { minLength: true };
  if (numericValue.length > 15) return { maxLength: true };

  return null;
};

/**
 * Service that manages the signup flow v2 process.
 * Handles multi-step form data, validation, and account creation.
 */
@Injectable({ providedIn: 'root' })
export class SignupV2Service {

  // ===== INJECTED SERVICES =====
  private paymentMethodService = inject(PaymentMethodService);
  private fb = inject(FormBuilder);
  private accountService = inject(AccountService);
  private addressService = inject(AddressService);
  private klaviyoService = inject(KlaviyoService);
  private firebaseCrudService = inject(FirebaseCrudService);
  private loadingService = inject(LoadingService);
  private modalContentService = inject(ModalContentService);
  private localStorageService = inject(LocalStorageService);
  private router = inject(Router);
  private productsService = inject(ProductsService);
  private apiService = inject(ApiService);
  private location = inject(Location);
  private subscriptionsService = inject(SubscriptionsService);
  private deliveriesService = inject(DeliveriesService);
  private dataLayerService = inject(DataLayerService);
  private signalsStoreService = inject(SignalsStoreService);

  // ===== DELIVERY ADDRESS SIGNALS =====
  private deliveryAddress: WritableSignal<any> = signal(null);
  getDeliveryAddress: Signal<any> = computed(() => this.deliveryAddress());
  setDeliveryAddress = (address: any) => { this.deliveryAddress.set(address); }

  private deliveryOptions: WritableSignal<DeliveryOptionsResponseItem[]> = signal([]);
  getDeliveryOptions: Signal<DeliveryOptionsResponseItem[]> = computed(() => this.deliveryOptions());
  setDeliveryOptions = (options: DeliveryOptionsResponseItem[]) => { this.deliveryOptions.set(options); }

  private deliveryCoverageMessage: WritableSignal<string | undefined> = signal(undefined);
  getDeliveryCoverageMessage: Signal<string | undefined> = computed(() => this.deliveryCoverageMessage());
  setDeliveryCoverageMessage = (message: string | undefined) => { this.deliveryCoverageMessage.set(message); }

  // ===== FLOW STATE MANAGEMENT =====
  private isEmailValid: boolean = false;
  private progressLoaded = false;

  private addressLoadedFromStorage = signal(false);
  getAddressLoadedFromStorage: Signal<boolean> = computed(() => this.addressLoadedFromStorage());
  setAddressLoadedFromStorage = (loaded: boolean) => { this.addressLoadedFromStorage.set(loaded); }

  // ===== SIGNUP FLOW STEPS =====
  readonly steps: SignupFlowStep[] = [
    { id: 1, key: 'delivery', title: 'Delivery Address' },
    { id: 2, key: 'availability', title: 'Availability' },
    { id: 3, key: 'produce', title: 'Produce Box' },
    { id: 4, key: 'subscriptions', title: 'Subscriptions' },
    { id: 5, key: 'personal', title: 'Personal Info' },
    { id: 6, key: 'payment', title: 'Payment', last: true }
  ];

  // Current step index signal (allows flow control from any component)
  private currentStepIndexSignal: WritableSignal<number> = signal(0);
  getCurrentStepIndex: Signal<number> = computed(() => this.currentStepIndexSignal());
  setCurrentStepIndex(index: number) {
    const maxIndex = this.hasSelectedProducts() || this.getIsAbandonedFlow() ? this.steps.length - 1 : this.steps.length - 2; // Adjust max index based on product selection
    if (index < 0 || index > maxIndex) return;
    this.currentStepIndexSignal.set(index);
  }

  /**
   * Navigate to a step if allowed (only backward navigation or staying in place)
   * Forward navigation is handled via next/markCompleted methods
   * If the current step is payment, navigation is not allowed
   */
  goToIfAllowed(index: number) {
    const currentIndex = this.currentStepIndexSignal();
    if (this.steps[currentIndex].key === 'payment') return;

    if (index < currentIndex || (index > currentIndex && this.steps[index - 1].completed)) this.setCurrentStepIndex(index);
  }

  // ===== REACTIVE FORMS =====
  // Centralized forms to allow forward/backward navigation without losing data

  /** Delivery address form with email and address fields */
  addressForm = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    address2: [''],
    city: ['', Validators.required],
    state: ['', Validators.required],
    zip: ['', [Validators.required, Validators.pattern(/^[0-9]{5}$/)]]
  });

  /** Availability form for delivery day and time window selection */
  availabilityForm = this.fb.group({
    day: [null as (string | number | null), Validators.required],
    window: [null as (string | number | null), Validators.required],
    notes: ['']
  });

  /** Personal information form including user details and optional second user */
  personalForm = this.fb.group({
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    phone: ['', [Validators.required, phoneLengthValidator]],
    password: ['', [Validators.required, passwordStrengthValidator]],
    password2: ['', [Validators.required]],
    howHeardSource: [null as (string | number | null), Validators.required],
    howHeardDetails: [''],
    addAnotherUser: [false],
    // Second user fields
    secondUserFirstName: [''],
    secondUserLastName: [''],
    secondUserEmail: [''],
    secondUserPhone: ['', phoneLengthValidator],
    optInSms: [true],
    optInMarketing: [true]
  });

  /** Payment method form with billing information */
  paymentForm = this.fb.group({
    cardNumber: ['', Validators.required],
    exp: ['', [Validators.required, Validators.pattern(/^(0[1-9]|1[0-2])\/(\d{4})$/)]],
    cvc: ['', Validators.required],
    billingName: ['', Validators.required],
    billingAddressDifferent: [false],
    billingAddress1: [''],
    billingAddress2: [''],
    billingCity: [''],
    billingState: [''],
    billingZip: [''],
    terms: [false]
  });


  // ===== REFERENCE DATA =====
  private states: WritableSignal<{ id: string, name: string }[]> = signal([]);
  getStates: Signal<{ id: string, name: string }[]> = computed(() => this.states());

  // ===== PRODUCE BOXES CONFIGURATION =====
  // Hardcoded IDs for signup flow produce boxes
  private produceBoxIds: { id: number; group: 'customizable' | 'non-customizable'; order: number; meta: { priceRange: string; items: string; serves: string; mostPopular?: boolean; save10?: boolean, image?: string }; }[] = [
    { id: 39838, group: 'customizable', order: 1, meta: { priceRange: '$40.00 - $45.00', items: '7-10 items', serves: 'Serves 2 - 3 People', mostPopular: true, image: '/assets/images/signup-v2/produce-boxes/small-customizable.png' } },
    { id: 39837, group: 'customizable', order: 2, meta: { priceRange: '$50.00 - $55.00', items: '9-12 items', serves: 'Serves 3 - 4 People', image: '/assets/images/signup-v2/produce-boxes/large-customizable.png' } },
    { id: 39846, group: 'non-customizable', order: 3, meta: { priceRange: '$40.00 - $45.00', items: '7-10 items', serves: 'Serves 3 - 4 People', mostPopular: false, save10: true, image: '/assets/images/signup-v2/produce-boxes/large-harvest.png' } },
    { id: 39849, group: 'non-customizable', order: 4, meta: { priceRange: '$30.00 - $35.00', items: '7-10 items', serves: 'Serves 1 - 2 People', image: '/assets/images/signup-v2/produce-boxes/mini-harvest.png' } }
  ];

  private produceBoxes: WritableSignal<any[]> = signal([]);
  getProduceBoxes: Signal<any[]> = computed(() => this.produceBoxes());

  private selectedProduceBoxIds: WritableSignal<number[]> = signal([]);
  getSelectedProduceBoxIds: Signal<number[]> = computed(() => this.selectedProduceBoxIds());

  private isProduceBoxesLoading: WritableSignal<boolean> = signal(false);
  getIsProduceBoxesLoading: Signal<boolean> = computed(() => this.isProduceBoxesLoading());
  setIsProduceBoxesLoading = (loading: boolean) => { this.isProduceBoxesLoading.set(loading); }

  // ===== SUBSCRIPTIONS AND PRODUCTS =====
  // Persistent signal for Step 4 (Extra Subscriptions)
  private extraSubscriptions: WritableSignal<Product[]> = signal<Product[]>([]);
  getExtraSubscriptions: Signal<Product[]> = computed(() => this.extraSubscriptions());

  private selectedExtraSubscriptions: WritableSignal<number[]> = signal([]);
  getSelectedExtraSubscriptions: Signal<number[]> = computed(() => this.selectedExtraSubscriptions());

  private howHeardSource: WritableSignal<Array<Reference>> = signal([]);
  getHowHeardSource: Signal<Array<Reference>> = computed(() => this.howHeardSource());

  private frequencies: WritableSignal<Frequencies[]> = signal([]);
  getFrequencies: Signal<Frequencies[]> = computed(() => this.frequencies());

  // Frequency selection storage for produce boxes and subscriptions
  private produceBoxFrequencies: WritableSignal<{ [key: number]: Frequencies }> = signal({});
  private subscriptionFrequencies: WritableSignal<{ [key: number]: Frequencies }> = signal({});

  private isExtraSubscriptionsLoading: WritableSignal<boolean> = signal(false);
  getIsExtraSubscriptionsLoading: Signal<boolean> = computed(() => this.isExtraSubscriptionsLoading());
  setIsExtraSubscriptionsLoading = (loading: boolean) => { this.isExtraSubscriptionsLoading.set(loading); }

  // Value to check if user has selected any products from produce or subscriptions steps
  hasSelectedProducts: Signal<boolean> = computed(() => this.getSelectedProduceBoxIds().length > 0 || this.getSelectedExtraSubscriptions().length > 0);

  isAbandonedFlow: WritableSignal<boolean> = signal(false);
  getIsAbandonedFlow: Signal<boolean> = computed(() => this.isAbandonedFlow());

  // ===== CONSTRUCTOR =====
  constructor() {
    this.paymentMethodService.getStates().subscribe(states => this.states.set(states));

    // Subscription to apply/remove dynamic validation on howHeardDetails
    this.personalForm.get('howHeardSource')?.valueChanges.subscribe(val => {
      const ref = this.getHowHeardSource().find(r => r.id === val);
      const detailsCtrl = this.personalForm.get('howHeardDetails');
      if (!detailsCtrl) return;
      if (ref?.hasAdditionalInfo) {
        detailsCtrl.addValidators(Validators.required);
      } else {
        detailsCtrl.removeValidators(Validators.required);
        detailsCtrl.setValue('');
      }
      detailsCtrl.updateValueAndValidity({ emitEvent: false });
    });

    // Subscription to apply/remove dynamic validation on second user fields
    this.personalForm.get('addAnotherUser')?.valueChanges.subscribe(addAnother => {
      const secondUserFields = ['secondUserFirstName', 'secondUserLastName', 'secondUserEmail', 'secondUserPhone'];

      secondUserFields.forEach(fieldName => {
        const control = this.personalForm.get(fieldName);
        if (!control) return;

        if (addAnother) {
          if (fieldName === 'secondUserEmail') {
            control.addValidators([Validators.required, Validators.email]);
          } else {
            control.addValidators(Validators.required);
          }
        } else {
          control.removeValidators([Validators.required, Validators.email]);
          control.setValue('');
        }
        control.updateValueAndValidity({ emitEvent: false });
      });
    });

    // Subscription to apply/remove dynamic validation on billing address fields
    this.paymentForm.get('billingAddressDifferent')?.valueChanges.subscribe(isDifferent => {
      const billingFields = ['billingAddress1', 'billingCity', 'billingState', 'billingZip'];

      billingFields.forEach(fieldName => {
        const control = this.paymentForm.get(fieldName);
        if (!control) return;

        if (isDifferent) {
          control.addValidators(Validators.required);
        } else {
          control.removeValidators(Validators.required);
          control.setValue('');
        }
        control.updateValueAndValidity({ emitEvent: false });
      });
    });

    this.validateAbandonedFlow();
  }

  /**
   * Validate abandoned flow and load progress if needed
   */
  private async validateAbandonedFlow() {
    const sessionStored: Session = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION) as any
    const hasPaymentMethod = !!(sessionStored)?.settings?.hasPaymentMethod;
    const stepToLoad = this.signalsStoreService.hasSession() && !hasPaymentMethod ? 5 : 0
    if (stepToLoad > 0) {
      this.isAbandonedFlow.set(true);
      const email = sessionStored?.accountInfo?.email;
      if (!email) return;
      await this.loadProgress(email);
      this.loadProduceBoxesIfNeeded();
      this.loadSubscriptionProductsIfNeeded();
    } else {
      this.isAbandonedFlow.set(false);
    }
  }

  // ===== PRODUCE BOX MANAGEMENT =====
  /**
   * Toggle selection of a produce box by ID
   */
  toggleProduceBox(id: number) {
    const current = this.selectedProduceBoxIds();
    this.selectedProduceBoxIds.set(current.includes(id) ? current.filter(x => x !== id) : [...current, id]);
  }

  /**
   * Toggle selection of an extra subscription by ID
   */
  toggleExtraSubscription(id: number) {
    const current = this.selectedExtraSubscriptions();
    this.selectedExtraSubscriptions.set(current.includes(id) ? current.filter(x => x !== id) : [...current, id]);
  }

  // ===== FREQUENCY MANAGEMENT =====
  /**
   * Set frequency for a specific produce box
   * @param boxId Produce box ID
   * @param frequency Frequency object to set
   */
  setProduceBoxFrequency(boxId: number, frequency: Frequencies) {
    const current = this.produceBoxFrequencies();
    this.produceBoxFrequencies.set({ ...current, [boxId]: frequency });
  }

  /**
   * Set frequency for a specific subscription
   * @param subscriptionId Subscription ID
   * @param frequency Frequency object to set
   */
  setSubscriptionFrequency(subscriptionId: number, frequency: Frequencies) {
    const current = this.subscriptionFrequencies();
    this.subscriptionFrequencies.set({ ...current, [subscriptionId]: frequency });
  }

  /**
   * Get frequency for a specific produce box
   * @param boxId Produce box ID
   * @returns Frequency object or default frequency
   */
  getProduceBoxFrequency(boxId: number): Frequencies | null {
    return this.produceBoxFrequencies()[boxId] || this.frequencies()[0];
  }

  /**
   * Get frequency for a specific subscription
   * @param subscriptionId Subscription ID
   * @returns Frequency object or default frequency
   */
  getSubscriptionFrequency(subscriptionId: number): Frequencies | null {
    return this.subscriptionFrequencies()[subscriptionId] || this.frequencies()[0];
  }

  /**
   * Load frequencies from API if not already loaded
   * @returns Promise that resolves when frequencies are loaded
   */
  loadFrequenciesIfNeeded() {
    if (this.frequencies().length) return Promise.resolve();
    const params: RequestHandlerParams = {
      method: 'GET',
      endpoint: '/subscriptions/frequencies',
      apiV3: true,
    };

    return new Promise<void>((resolve) => {
      this.apiService
        .handleRequest(params)
        .pipe(
          filter((response: any) => response?.length),
          tap((response: any) => {
            this.frequencies.set(response);
            resolve();
          }),
          catchError(() => {
            resolve();
            return of(null);
          })
        )
        .subscribe();
    });
  }

  /**
   * Load produce boxes from API if not already loaded
   */
  loadProduceBoxesIfNeeded(): Promise<void> {
    if (this.produceBoxes().length) return Promise.resolve();

    return new Promise<void>((resolve) => {
      this.setIsProduceBoxesLoading(true);
      const ids = this.produceBoxIds.map(x => x.id);
      this.productsService.getProductsDetailsByIds(ids).subscribe((products: any[]) => {
        const enriched = products.map((p: any) => {
          const cfg = this.produceBoxIds.find(c => c.id === (p as any).id);
          return { ...p, _signup: cfg };
        }).sort((a, b) => a._signup.order - b._signup.order);
        this.produceBoxes.set(enriched);
        const saved = (this as any)._savedProduceIds as number[] | undefined;
        if (saved?.length) {
          this.selectedProduceBoxIds.set(saved);
          // Initialize frequencies for saved produce boxes
          this.initializeFrequenciesForSavedProduceBoxes(saved);
        }
        this.setIsProduceBoxesLoading(false);
        resolve();
      });
    });
  }

  /**
   * Initialize frequencies for saved produce boxes with default frequency
   * @param savedIds Array of saved produce box IDs
   */
  private initializeFrequenciesForSavedProduceBoxes(savedIds: number[]) {
    const frequencies = this.frequencies();
    if (frequencies.length === 0) return;

    const currentFrequencies = this.produceBoxFrequencies();
    const updatedFrequencies = { ...currentFrequencies };

    savedIds.forEach(id => {
      if (!updatedFrequencies[id]) {
        updatedFrequencies[id] = frequencies[0];
      }
    });

    this.produceBoxFrequencies.set(updatedFrequencies);
  }

  /**
   * Load subscription products from API if not already loaded
   */
  loadSubscriptionProductsIfNeeded(): Promise<void> {
    if (this.extraSubscriptions().length) return Promise.resolve();
    this.setIsExtraSubscriptionsLoading(true);
    return new Promise<void>((resolve) => {
      this.productsService.getSignupProducts().subscribe((data: ApiResponse<Product[]>) => {
        const formatted = this.formatSubscriptionProducts(data.data || []);
        this.extraSubscriptions.set(formatted);
        const saved = (this as any)._savedExtraSubscriptions as number[] | undefined;
        if (saved?.length) {
          this.selectedExtraSubscriptions.set(saved);
          // Initialize frequencies for saved subscriptions
          this.initializeFrequenciesForSavedSubscriptions(saved);
        }
        this.setIsExtraSubscriptionsLoading(false);
        resolve();
      });
    });
  }

  /**
   * Initialize frequencies for saved subscriptions with default frequency
   * @param savedIds Array of saved subscription IDs
   */
  private initializeFrequenciesForSavedSubscriptions(savedIds: number[]) {
    const frequencies = this.frequencies();
    if (frequencies.length === 0) return;

    const currentFrequencies = this.subscriptionFrequencies();
    const updatedFrequencies = { ...currentFrequencies };

    savedIds.forEach(id => {
      if (!updatedFrequencies[id]) {
        updatedFrequencies[id] = frequencies[0];
      }
    });

    this.subscriptionFrequencies.set(updatedFrequencies);
  }

  /**
   * Format subscription products by expanding attributes and packages into individual products
   * @param products Array of products to format
   * @returns Array of formatted products with proper variant and package information
   */
  formatSubscriptionProducts(products: Product[]): Product[] {
    const formattedProducts: Product[] = [];

    products.forEach(product => {
      // If product has attributes array, create separate products for each stock item
      if (product.attributes && Array.isArray(product.attributes) && product.attributes.length > 0) {
        product.attributes.forEach(attribute => {
          const attributeValues = attribute.values;
          attributeValues.forEach(attributeValue => {
            formattedProducts.push({
              ...product,
              variantId: attributeValue.variantId || product.variantId, // Keep only the specific stock info for this variant
              price: attributeValue.price || product.price,
              display: attributeValue.name || product.display,
              originalPrice: attributeValue.originalPrice || product.originalPrice,
              isOnSale: attributeValue.isOnSale || product.isOnSale,
              id: attributeValue.variantId || product.id
            });
          });
        });
      } else {
        // If no attributes or empty array, validate packages
        if (product.packages && Array.isArray(product.packages) && product.packages.length > 0) {
          product.packages.forEach(productPackage => {
            formattedProducts.push({
              ...product,
              display: productPackage.name || product.display,
              price: productPackage.price || product.price,
              originalPrice: productPackage.originalPrice || product.originalPrice,
              isOnSale: productPackage.isOnSale || product.isOnSale,
              packageId: +productPackage.id || product.packageId,
              variantId: product.stockInfo?.[0]?.variantId || product.variantId,
              id: +productPackage.id || product.id
            });
          });
        } else {
          formattedProducts.push({ ...product, variantId: product.stockInfo?.[0]?.variantId || product.variantId, id: product.id });
        }
      }
    });

    return formattedProducts;
  }

  // ===== STEP VALIDATION AND NAVIGATION =====

  /**
   * Check if the user can proceed to the next step based on current step validation
   */
  canProceed(stepKey: string): boolean {
    switch (stepKey) {
      case 'delivery': return this.addressForm.valid && this.deliveryAddress() && this.isEmailValid;
      case 'availability': return this.availabilityForm.valid;
      case 'produce': return true; // Optional to select produce box
      case 'subscriptions': return true; // Optional
      case 'personal': return this.personalForm.valid && this.passwordsMatch();
      case 'payment': return this.paymentForm.valid;
      default: return true;
    }
  }

  /**
   * Mark a step as completed and execute the corresponding completion logic
   * @param stepKey The key of the step to mark as completed
   * @returns Promise<boolean> indicating if the step was successfully completed
   */
  async markCompleted(stepKey: string): Promise<boolean> {
    return new Promise<boolean>(async (resolve) => {
      const step = this.steps.find(s => s.key === stepKey);
      if (step) step.completed = true;

      let isCompleted = true;
      switch (stepKey) {
        case 'delivery': await this.afterDeliveryAddressStepCompleted(stepKey); break;
        case 'availability': await this.afterAvailabilityStepCompleted(stepKey); break;
        case 'produce': await this.afterProduceStepCompleted(stepKey); break;
        case 'subscriptions': await this.afterSubscriptionsStepCompleted(stepKey); break;
        case 'personal': isCompleted = await this.afterPersonalInfoStepCompleted(stepKey, step!); break;
        case 'payment': await this.afterPaymentMethodStepCompleted(stepKey); break;
      }

      resolve(isCompleted);
    });
  }

  // ===== STEP COMPLETION HANDLERS =====
  /**
   * Handle completion of the delivery address step
   * Saves address information and tracks analytics event
   */
  private async afterDeliveryAddressStepCompleted(stepKey: string) {
    const email = this.addressForm.get('email')?.value!;

    // Create Klaviyo profile with email only
    await this.klaviyoService.createProfile(email);

    // Track Started Registration event
    this.klaviyoService.trackSignupEvent(KLAVIYOEVENTS.StartedRegistration, {}, email);

    // Track Sign Up Funnel event to Data Layer
    this.dataLayerService.trackSignUpFunnelDeliveryAddressEvent(this.getUserDataLayer());
    this.dataLayerService.trackSignUpFunnelAvailabilityEvent(this.getUserDataLayer());

    await this.saveStepInfo(
      {
        step: stepKey,
        email: email,
        address2: this.addressForm.get('address2')?.value!,
        city: this.addressForm.get('city')?.value!,
        state: this.addressForm.get('state')?.value!,
        zip: this.addressForm.get('zip')?.value!,
        address: this.addressService.navegoAddress()
      },
      email);
  }

  /**
   * Handle completion of the availability step
   * Saves delivery day and time window preferences
   */
  private async afterAvailabilityStepCompleted(stepKey: string) {
    const email = this.addressForm.get('email')?.value!;
    const dayId = this.availabilityForm.get('day')?.value;
    const windowId = this.availabilityForm.get('window')?.value;

    // Get delivery day and time window names
    const deliveryDay = this.getDeliveryOptions().find(d => d.id === dayId)?.name || 'Unknown';
    const timeWindow = this.getDeliveryOptions().find(d => d.id === dayId)?.timeSet?.find((tw: any) => tw.id === windowId)?.name || 'Unknown';

    // Track Sign Up - Step 2 event
    this.klaviyoService.trackSignupEvent(KLAVIYOEVENTS.SignUpStep2, {
      "Delivery Day": deliveryDay,
      "Time Slot": timeWindow
    }, email);

    this.dataLayerService.trackSignUpFunnelProduceBoxEvent(this.getUserDataLayer());

    await this.saveStepInfo(
      {
        step: stepKey,
        day: dayId,
        window: windowId,
        notes: this.availabilityForm.get('notes')?.value
      },
      email
    );
  }

  /**
   * Handle completion of the produce box selection step
   * Saves selected produce boxes and their frequencies
   */
  private async afterProduceStepCompleted(stepKey: string) {
    const email = this.addressForm.get('email')?.value!;
    const selectedBoxIds = this.getSelectedProduceBoxIds();
    const produceBoxes = this.getProduceBoxes();

    // Prepare produce box data for Klaviyo event
    const produceBoxData = selectedBoxIds.map(boxId => {
      const box = produceBoxes.find(b => b.id === boxId);
      const frequency = this.getProduceBoxFrequency(boxId);
      return {
        "Name": box?.name || box?.display || 'Unknown',
        "Frequency": frequency?.name || 'Unknown'
      };
    });

    // Track Sign Up - Step 3 event
    this.klaviyoService.trackSignupEvent(KLAVIYOEVENTS.SignUpStep3, {
      "Produce Box Selected": selectedBoxIds.length > 0,
      "Products": produceBoxData,
    }, email);

    this.dataLayerService.trackSignUpFunnelSubscriptionsEvent(this.getUserDataLayer());

    await this.saveStepInfo(
      {
        step: stepKey,
        produceSelectedIds: selectedBoxIds,
        produceBoxFrequencies: this.produceBoxFrequencies()
      },
      email
    );
  }

  /**
   * Handle completion of the subscriptions step
   * Saves selected extra subscriptions and their frequencies
   */
  private async afterSubscriptionsStepCompleted(stepKey: string) {
    const email = this.addressForm.get('email')?.value!;
    const selectedSubscriptionIds = this.getSelectedExtraSubscriptions();
    const subscriptions = this.getExtraSubscriptions();

    // Prepare subscription data for Klaviyo event
    const subscriptionData = selectedSubscriptionIds.map(subId => {
      const subscription = subscriptions.find(s => s.id === subId);
      const frequency = this.getSubscriptionFrequency(subId);
      return {
        "Name": subscription?.name || subscription?.display || 'Unknown',
        "Frequency": frequency?.name || 'Unknown'
      };
    });

    // Track Sign Up - Step 4 event
    this.klaviyoService.trackSignupEvent(KLAVIYOEVENTS.SignUpStep4, {
      "Extra Subscription Selected": selectedSubscriptionIds.length > 0,
      "Additional Products Selected": subscriptionData,
    }, email);

    this.dataLayerService.trackSignUpFunnelPersonalInfoEvent(this.getUserDataLayer());

    await this.saveStepInfo(
      {
        step: stepKey,
        subscriptionsSelected: selectedSubscriptionIds,
        subscriptionFrequencies: this.subscriptionFrequencies()
      },
      email
    );
  }

  /**
   * Handle completion of the personal information step
   * Creates the client account and saves personal information
   * @param stepKey The step key
   * @param step The step object for navigation control
   * @returns Promise<boolean> indicating if the step was successfully completed
   */
  private async afterPersonalInfoStepCompleted(stepKey: string, step: SignupFlowStep): Promise<boolean> {
    return new Promise<boolean>(async (resolve) => {
      const email = this.addressForm.get('email')?.value!;


      const isClientRegistered = await this.createClient();
      if (!isClientRegistered) {
        // Go back to personal info step and mark the step as not completed
        this.setCurrentStepIndex(this.steps.findIndex(s => s.key === step.key));
        step.completed = false;
        resolve(false);
      }

      this.getMembershipPermissions();

      // Track Sign Up - Step 5 event
      this.klaviyoService.trackSignupEvent(KLAVIYOEVENTS.SignUpStep5, {}, email);

      this.dataLayerService.trackSignupFunnelPaymentMethodEvent(this.getUserDataLayer());

      await this.saveStepInfo(
        {
          step: stepKey,
          firstName: this.personalForm.get('firstName')?.value,
          lastName: this.personalForm.get('lastName')?.value,
          phone: this.personalForm.get('phone')?.value,
          howHeardSource: this.personalForm.get('howHeardSource')?.value,
          howHeardDetails: this.personalForm.get('howHeardDetails')?.value,
          addAnotherUser: this.personalForm.get('addAnotherUser')?.value,
          // Second user data
          secondUserFirstName: this.personalForm.get('secondUserFirstName')?.value,
          secondUserLastName: this.personalForm.get('secondUserLastName')?.value,
          secondUserEmail: this.personalForm.get('secondUserEmail')?.value,
          secondUserPhone: this.personalForm.get('secondUserPhone')?.value,
          optInSms: this.personalForm.get('optInSms')?.value,
          optInMarketing: this.personalForm.get('optInMarketing')?.value
        },
        email
      );

      this.deliveriesService.getDeliveryZoneInfo().subscribe();

      // If no products are selected, complete the flow and redirect to shop
      if (!this.hasSelectedProducts()) {
        await this.completeSignupFlowWithoutPayment();
      }

      resolve(true);
    });
  }

  // ===== CLIENT CREATION =====
  /**
   * Create a new client account with the collected signup data
   * @returns Promise<boolean> indicating if the client was successfully created
   */
  private async createClient(): Promise<boolean> {
    return new Promise<boolean>(async (resolve) => {
      const payload = this.setUpNewClientPayload();
      if (!payload) return resolve(false);
      this.accountService.create(payload).subscribe({
        next: (res: ApiResponse<Session>) => {
          resolve(!!res.data);
        },
        error: () => resolve(false)
      });
    });
  }

  private async getMembershipPermissions() {
    this.accountService.getMembershipPermissions().subscribe();
  }

  /**
   * Set up the payload for creating a new client account
   * Combines all form data into the required API structure
   * @returns NewAccountPayload object or null if required data is missing
   */
  private setUpNewClientPayload() {
    const payload: NewAccountPayload = {
      isExplorer: !this.hasSelectedProducts(),
      accountInfo: {
        email: this.addressForm.get('email')?.value!,
        dietaryPreferences: [],
        firstName: this.personalForm.get('firstName')?.value!,
        lastName: this.personalForm.get('lastName')?.value!,
        noteDriver: this.availabilityForm.get('notes')?.value || '',
        password: this.personalForm.get('password')?.value!,
        phone: this.personalForm.get('phone')?.value!
      },
      address: {
        additional: this.addressForm.get('address2')?.value || '',
        city: this.addressForm.get('city')?.value || '',
        latitude: this.addressService.navegoAddress()?.latitude,
        longitude: this.addressService.navegoAddress()?.longitude,
        stateCode: this.addressForm.get('state')?.value || '',
        street: this.addressService.navegoAddress()?.street || '',
        zip: this.addressForm.get('zip')?.value || '',
        noteDriver: this.availabilityForm.get('notes')?.value || '',
        zoneId: this.addressService.navegoAddress()?.zoneId
      },
      settings: {
        receiveNewsletter: this.personalForm.get('optInMarketing')?.value || false,
        receiveText: this.personalForm.get('optInSms')?.value || false,
        receiveTextDelivery: true,
        receiveSMS: false,
        receiveInfo: false,
        referral: this.personalForm.get('howHeardSource')?.value ? {
          source: this.personalForm.get('howHeardSource')?.value as string,
          additionalInfo: this.personalForm.get('howHeardDetails')?.value || ''
        } : undefined,
        delivery: {
          dayId: +this.availabilityForm.get('day')?.value!,
          timeWindowId: +this.availabilityForm.get('window')?.value!
        }
      },
      contact: this.personalForm.get('addAnotherUser')?.value ? {
        firstName: this.personalForm.get('secondUserFirstName')?.value!,
        lastName: this.personalForm.get('secondUserLastName')?.value!,
        email: this.personalForm.get('secondUserEmail')?.value!,
        phone: this.personalForm.get('secondUserPhone')?.value!
      } : undefined,
    }
    return payload;
  }

  /**
   * Complete signup flow without payment step when no products are selected
   * Cleans up signup data and redirects to shop
   */
  private async completeSignupFlowWithoutPayment() {
    // Register klaviyo event
    const session: Session | null = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION)
    if (!session) return;
    if (session && session.deliveryInfo?.deliveryDay) {
      // Sign Up Date Config:
      const currentDate = new Date();
      const currentMonth = (currentDate.getMonth() + 1).toString().padStart(2, '0');
      const currentDay = (currentDate.getDate()).toString().padStart(2, '0');
      const signupDate = `${currentDate.getFullYear()}-${currentMonth}-${currentDay}`;
      const formattedSignupDate = formatDateToReadableString(signupDate).mmddyyyyFormat.replace(/\//g, "-");
      // ***********************
    };

    // Remove the firebase info about the user since we have the client created
    await this.firebaseCrudService.delete(FIREBASE_COLLECTIONS.SIGNUP_STEPS, this.addressForm.get('email')?.value!);

    // Register data layer event
    this.dataLayerService.trackSignUpEvent();

    // Set new account flag
    this.localStorageService.set(LOCALSTORAGE_KEYS.NEW_ACCOUNT, true);

    // Redirect to shop
    this.location.replaceState('/shop');
    window.location.reload();
  }

  /**
   * Handle completion of the payment method step
   * Cleans up signup data and redirects to shop
   */
  private async afterPaymentMethodStepCompleted(stepKey: string) {
    const email = this.addressForm.get('email')?.value!;
    const session: Session | null = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION);

    if (!session) return;


    if (session && session.deliveryInfo?.deliveryDay) {
      // Sign Up Date Config:
      const currentDate = new Date();
      const currentMonth = (currentDate.getMonth() + 1).toString().padStart(2, '0');
      const currentDay = (currentDate.getDate()).toString().padStart(2, '0');
      const signupDate = `${currentDate.getFullYear()}-${currentMonth}-${currentDay}`;
      const formattedSignupDate = formatDateToReadableString(signupDate).mmddyyyyFormat.replace(/\//g, "-");
      // ***********************

      // Track Registration Completed event
      this.klaviyoService.trackSignupEvent(KLAVIYOEVENTS.RegistrationCompleted, {}, email);
    };

    // Remove the firebase info about the user since we have the client created
    await this.firebaseCrudService.delete(FIREBASE_COLLECTIONS.SIGNUP_STEPS, email);

    // Register data layer event
    this.dataLayerService.trackSignUpFunnelAccountSuccessEvent(this.getUserDataLayer());

    // Set new account flag
    this.localStorageService.set(LOCALSTORAGE_KEYS.NEW_ACCOUNT, true);

    // Redirect to shop
    this.location.replaceState('/shop');
    window.location.reload();
  }

  // ===== UTILITY METHODS =====

  /**
   * Save step information to Firebase for persistence across sessions
   * @param payload Step data to save
   * @param email User email as document ID
   */
  private async saveStepInfo(payload: any, email: string): Promise<void> {
    const exists = await this.firebaseCrudService.existsDoc(FIREBASE_COLLECTIONS.SIGNUP_STEPS, email);

    payload.timestamp = new Date().toISOString();

    if (exists)
      await this.firebaseCrudService.update(FIREBASE_COLLECTIONS.SIGNUP_STEPS, email, payload);
    else
      await this.firebaseCrudService.add(FIREBASE_COLLECTIONS.SIGNUP_STEPS, email, payload);
  }

  /**
   * Check if password and confirm password fields match
   * @returns boolean indicating if passwords match
   */
  passwordsMatch(): boolean {
    const p1 = this.personalForm.get('password')?.value;
    const p2 = this.personalForm.get('password2')?.value;
    return !!p1 && p1 === p2;
  }

  /**
   * Get the display text for the selected delivery day
   * @returns string with the delivery day name or fallback text
   */
  getDeliveryDayText(): string {
    const dayId = this.availabilityForm.get('day')?.value;
    const dayName = this.getDeliveryOptions().find(d => d.id === dayId)?.name;
    if (!dayName) return 'your delivery day';
    return dayName;
  }

  /**
   * Get placeholder text for driver name
   * @returns string with driver name placeholder
   */
  getDriversPlaceholder(): { name: string, image: string, expertise: string }[] {
    // Placeholder; could be integrated with a real driver assignment system in the future
    return [{
      name: 'James',
      image: '/assets/images/signup-v2/personal-info/james.webp',
      expertise: '5+ years'
    }, {
      name: 'Scotty',
      image: '/assets/images/signup-v2/personal-info/scotty.webp',
      expertise: '6+ years'
    }, {
      name: 'Saoirse',
      image: '/assets/images/signup-v2/personal-info/saoirse.webp',
      expertise: '1+ year'
    }, {
      name: 'Devon',
      image: '/assets/images/signup-v2/personal-info/devon.webp',
      expertise: '6+ years'
    }, {
      name: 'Nat',
      image: '/assets/images/signup-v2/personal-info/nat.webp',
      expertise: '3+ months'
    }, {
      name: 'Alex',
      image: '/assets/images/signup-v2/personal-info/alex.webp',
      expertise: '2+ years'
    }];
  }

  /**
   * Set up the delivery coverage message based on available delivery days
   * @param weekDays Array of delivery day names
   */
  private setupDeliveryCoverageMessage(weekDays: string[]) {
    const lastDay = weekDays.pop();
    const days = weekDays.join(', ');
    const lastComma = weekDays.length > 1 ? ',' : '';

    const message = `Yes! We Deliver to Your Home Address on ${days}${lastComma} and ${lastDay} Each Week`;
    this.deliveryCoverageMessage.set(message);
  }

  // ===== DATA LOADING METHODS =====
  /**
   * Fetch delivery options for the current address zone
   * @returns Promise that resolves when delivery options are loaded
   */
  async fetchDeliveryOptions(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.accountService.getDeliveryOptions(this.addressService.navegoAddress()?.zoneId).subscribe(
        {
          next: (options: DeliveryOptionsResponseItem[]) => {
            const days = options.map(o => o.name);
            this.setupDeliveryCoverageMessage(days);
            this.setDeliveryOptions(options);
            resolve();
          },
          error: () => resolve(),
          complete: () => resolve()
        }
      );
    });
  }

  /**
   * Load how heard sources if not already loaded
   */
  loadHeardSourcesIfNeeded(): void {
    if (this.howHeardSource().length) return;
    this.accountService.getAccountSettings().pipe(
      catchError(() => [])
    ).subscribe(settings => {
      if (settings?.references)
        this.howHeardSource.set(settings.references);
    });
  }

  // ===== ADDRESS MANAGEMENT =====
  /**
   * Clear address information from the form
   */
  clearAddressInfo() {
    this.setDeliveryCoverageMessage(undefined);
    this.addressForm.get('city')?.setValue('');
    this.addressForm.get('state')?.setValue('');
    this.addressForm.get('zip')?.setValue('');
  }

  /**
   * Set address information in the form from address object
   * @param address Address object containing city, stateCode, and zipCode
   */
  setAddressInfo(address: any) {
    this.addressForm.get('city')?.setValue(address.city);
    this.addressForm.get('city')?.markAsDirty();
    this.addressForm.get('state')?.setValue(address.stateCode);
    this.addressForm.get('state')?.markAsDirty();
    this.addressForm.get('zip')?.setValue(address.zipCode);
    this.addressForm.get('zip')?.markAsDirty();
  }

  // ===== EMAIL VALIDATION =====
  /**
   * Validate email existence and handle existing email scenarios
   * @param emailControl HTML input element for email field
   */
  validateEmail(emailControl: HTMLInputElement): void {
    const email = this.addressForm.get('email')?.value;
    if (!email) return;
    this.accountService.validateEmailExistence(email)
      .pipe(tap(emailExists => emailExists ? this.showModalEmailAlreadyInUse(email, emailControl) : (this.isEmailValid = true)))
      .subscribe(() => {
        if (this.isEmailValid) this.loadProgress(email);
      });
  }

  /**
   * Show modal when email is already in use
   * @param email The email that's already in use
   * @param emailControl HTML input element for email field
   */
  private showModalEmailAlreadyInUse(email: string, emailControl: HTMLInputElement) {
    this.modalContentService.openModal(ModalContentTypes.CONFIRMATION, {
      title: 'Email already in use',
      textContent: `Hey! Looks like you already have an account with us. Would you like to login with <b><i>${email}</i></b>?`,
      cancelButtonText: 'Try another email',
      confirmButtonText: 'Click here to login',
    }).closed
      .subscribe((res: { confirm: boolean } | null) => {
        if (!res?.confirm) return this.clearEmailField(emailControl);
        this.localStorageService.set(LOCALSTORAGE_KEYS.PRELOADED_EMAIL, email);
        this.router.navigate(['/login']);
      })
  }

  /**
   * Clear email field and reset validation state
   * @param emailControl HTML input element for email field
   */
  private clearEmailField(emailControl: HTMLInputElement) {
    this.addressForm.get('email')?.setValue('');
    this.isEmailValid = false;
    // Set focus on email input
    setTimeout(() => emailControl.focus(), 500);
  }

  // ===== MODAL AND FLOW MANAGEMENT =====
  /**
   * Open modal to confirm editing delivery address (which restarts the flow)
   */
  openEditDeliveryAddressModal() {
    this.modalContentService.openModal(ModalContentTypes.CONFIRMATION, {
      title: 'Edit delivery address',
      textContent: 'Editing your delivery address will restart the signup process. Do you want to continue?',
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Edit address'
    }).closed.subscribe((res: { confirm: boolean } | null) => {
      if (!res?.confirm) return;
      const currentEmail = this.addressForm.get('email')?.value || '';
      if (currentEmail) {
        this.firebaseCrudService.delete(FIREBASE_COLLECTIONS.SIGNUP_STEPS, currentEmail);
        this.localStorageService.remove(LOCALSTORAGE_KEYS.ANONYMOUS_ADDRESS);
        this.localStorageService.remove(LOCALSTORAGE_KEYS.GOOGLEPLACERESULT);
        this.signalsStoreService.googleAddress.set(null);
        this.signalsStoreService.anonymousUserAddress.set(null);
      }
      this.resetFlow();
    });
  }

  /**
   * Check if a specific step is completed
   * @param stepKey The key of the step to check
   * @returns boolean indicating if the step is completed
   */
  isStepCompleted(stepKey: string) { return !!this.steps.find(s => s.key === stepKey)?.completed; }

  // ===== PROGRESS LOADING AND RESET =====
  /**
   * Load saved progress from Firebase for the given email
   * @param email User email to load progress for
   */
  private async loadProgress(email: string): Promise<void> {
    if (this.progressLoaded) return Promise.resolve();
    return new Promise<void>(async (resolve) => {
      // Load frequencies first
      await this.loadFrequenciesIfNeeded();

      this.loadingService.open(email);

      this.firebaseCrudService.getById(FIREBASE_COLLECTIONS.SIGNUP_STEPS, email, async (data) => {

        if (!data) {
          this.loadingService.close(email);
          return;
        }

        this.setAddressLoadedFromStorage(true);
        this.progressLoaded = true;
        // Step 1 data
        if (data.email) {
          this.addressForm.patchValue({
            email: data.email,
            address2: data.address2 || '',
            city: data.city || '',
            state: data.state || '',
            zip: data.zip || ''
          });
          // Mark form controls as dirty to show validation states
          Object.keys(this.addressForm.controls).forEach(key => {
            const control = this.addressForm.get(key);
            if (control && control.value) control.markAsDirty();
          });
          // Set address in AddressService and fetch delivery options
          if (data.address) {
            this.setDeliveryAddress(data.address);
            this.addressService.navegoAddress.set(data.address);
            await this.fetchDeliveryOptions();
          }
          const deliveryStep = this.steps.find(s => s.key === 'delivery');
          if (deliveryStep) deliveryStep.completed = true;
        }
        // Step 2 data
        const availabilityStep = this.steps.find(s => s.key === 'availability');
        if (data.day) {
          const applyAvailability = () => {
            this.availabilityForm.patchValue({ day: data.day, window: data.window || null, notes: data.notes || '' });
            if (availabilityStep) availabilityStep.completed = true;
          };
          if (this.getDeliveryOptions().length) applyAvailability(); else setTimeout(applyAvailability, 500);
        }
        // Step 3 data
        if (data.produceSelectedIds?.length) {
          (this as any)._savedProduceIds = data.produceSelectedIds; // temp store until products load
          await this.loadProduceBoxesIfNeeded();
          if (data.produceBoxFrequencies) {
            this.produceBoxFrequencies.set(data.produceBoxFrequencies);
          } else {
            // Initialize with default frequencies if none saved
            const defaultFrequencies: { [key: number]: Frequencies } = {};
            data.produceSelectedIds.forEach((id: number) => {
              if (this.frequencies().length > 0) {
                defaultFrequencies[id] = this.frequencies()[0];
              }
            });
            this.produceBoxFrequencies.set(defaultFrequencies);
          }
          const produceStep = this.steps.find(s => s.key === 'produce');
          if (produceStep) produceStep.completed = true;
        }
        // Step 4 data
        if (data.subscriptionsSelected?.length) {
          (this as any)._savedExtraSubscriptions = data.subscriptionsSelected; // temp store until products load
          await this.loadSubscriptionProductsIfNeeded();
          if (data.subscriptionFrequencies) {
            this.subscriptionFrequencies.set(data.subscriptionFrequencies);
          } else {
            // Initialize with default frequencies if none saved
            const defaultFrequencies: { [key: number]: Frequencies } = {};
            data.subscriptionsSelected.forEach((id: number) => {
              if (this.frequencies().length > 0) {
                defaultFrequencies[id] = this.frequencies()[0];
              }
            });
            this.subscriptionFrequencies.set(defaultFrequencies);
          }
          const subscriptionsStep = this.steps.find(s => s.key === 'subscriptions');
          if (subscriptionsStep) {
            // if step 3 is not completed, complete it as well (user might have skipped produce selection)
            const produceStep = this.steps.find(s => s.key === 'produce');
            if (produceStep && !produceStep.completed) produceStep.completed = true;

            subscriptionsStep.completed = true
          };
        }

        // Step 5 data (personal info)
        if (data.firstName || data.lastName || data.phone) {
          this.personalForm.patchValue({
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            phone: data.phone || '',
            howHeardDetails: data.howHeardDetails || '',
            howHeardSource: data.howHeardSource || '',
            addAnotherUser: !!data.addAnotherUser,
            // Second user data
            secondUserFirstName: data.secondUserFirstName || '',
            secondUserLastName: data.secondUserLastName || '',
            secondUserEmail: data.secondUserEmail || '',
            secondUserPhone: data.secondUserPhone || '',
            optInSms: !!data.optInSms,
            optInMarketing: !!data.optInMarketing
          });
          const personalStep = this.steps.find(s => s.key === 'personal');
          if (personalStep) personalStep.completed = true;
        }

        setTimeout(() => {
          // Automatically select the next step after the last completed one
          const completedIndices = this.steps
            .map((s, idx) => ({ idx, completed: !!s.completed }))
            .filter(s => s.completed)
            .map(s => s.idx);
          let nextIndex = 0;
          if (completedIndices.length) {
            const maxCompleted = Math.max(...completedIndices);
            nextIndex = Math.min(maxCompleted + 1, this.steps.length - 1);
          }
          this.setCurrentStepIndex(nextIndex);
          this.loadingService.close(email);
          resolve();
        }, 1);
      });
    });
  }

  /**
   * Reset the entire signup flow to initial state
   */
  private resetFlow() {
    this.steps.forEach(s => s.completed = false);
    this.isEmailValid = false;
    this.progressLoaded = false;
    this.addressForm.reset({ email: this.addressForm.get('email')?.value || '' });
    this.availabilityForm.reset({ day: null, window: null, notes: '' });
    this.personalForm.reset();
    this.paymentForm.reset();
    this.setDeliveryAddress(null);
    this.setDeliveryOptions([]);
    this.setDeliveryCoverageMessage(undefined);
    this.selectedProduceBoxIds.set([]);
    this.produceBoxes.set([]);
    this.extraSubscriptions.set([]);
    this.produceBoxFrequencies.set({});
    this.subscriptionFrequencies.set({});
    this.setCurrentStepIndex(0);
  }

  // ===== PAYMENT AND SUBSCRIPTION MANAGEMENT =====
  /**
   * Handle successful payment method addition
   * Sends subscriptions and produce boxes to backend and completes the flow
   */
  async onPaymentMethodAdded() {
    // Send subscriptions and produce boxes to backend
    const isSent = await this.sendSubscriptionsAndProduceBoxesToBackend();
    if (!isSent) return;

    // Mark the payment step as completed
    const paymentStep = this.steps.find(s => s.key === 'payment');
    if (paymentStep) {
      paymentStep.completed = true;
    }

    // Execute the payment step completion logic
    await this.afterPaymentMethodStepCompleted('payment');
  }

  /**
   * Send selected subscriptions and produce boxes to the backend
   * @returns Promise<boolean> indicating if the operation was successful
   */
  private async sendSubscriptionsAndProduceBoxesToBackend(): Promise<boolean> {
    return new Promise<boolean>(async (resolve) => {
      const products = this.setUpSubscriptionsAndProduceBoxesPayload();
      if (!products) return resolve(true);
      this.subscriptionsService.createSubscriptionMultiple(products).pipe(
        tap(() => resolve(true)),
        catchError(() => {
          resolve(true);
          return of(true);
        })
      ).subscribe();
    });
  }

  /**
   * Set up the payload for subscriptions and produce boxes to send to backend
   * @returns Array of SubscriptionPayload objects or null if no products selected
   */
  private setUpSubscriptionsAndProduceBoxesPayload(): SubscriptionPayload[] | null {
    const subscriptions = this.extraSubscriptions()
      .filter(subscription => this.selectedExtraSubscriptions().includes(subscription.id))
      .map(subscription => {
        const frequency = this.getSubscriptionFrequency(subscription.id);
        const productWithFrequency = {
          ...subscription,
          subscription: frequency ? {
            frequency: {
              id: frequency.id,
              name: frequency.name
            }
          } : {
            frequency: {
              id: this.frequencies()[0].id,
              name: this.frequencies()[0].name
            }
          }
        };
        return this.setUpSubscriptionProduct(productWithFrequency);
      });

    const produceBoxes = this.produceBoxes()
      .filter(produceBox => this.selectedProduceBoxIds().includes(produceBox.id))
      .map(produceBox => {
        const frequency = this.getProduceBoxFrequency(produceBox.id);
        const productWithFrequency = {
          ...produceBox,
          subscription: frequency ? {
            frequency: {
              id: frequency.id,
              name: frequency.name
            }
          } : {
            frequency: {
              id: this.frequencies()[0].id,
              name: this.frequencies()[0].name
            }
          },
          variantId: produceBox.stockInfo?.[0]?.variantId || 0
        };
        return this.setUpSubscriptionProduct(productWithFrequency);
      });

    return [...subscriptions, ...produceBoxes] as SubscriptionPayload[];
  }

  /**
   * Set up individual subscription product payload
   * @param subscription Product object to convert to subscription payload
   * @returns SubscriptionPayload object or null if required data is missing
   */
  private setUpSubscriptionProduct(subscription: Product): SubscriptionPayload | null {
    const frequencyId = subscription.subscription?.frequency?.id || undefined;
    let startDate = subscription.subscription?.startDate || undefined;

    if (!startDate) {
      const sesion: Session | null = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION);
      startDate = sesion?.deliveryInfo?.deliveryDate ?? undefined;
    }

    if (!frequencyId || !startDate)
      return null;

    return {
      frequencyId,
      packageId: subscription.packageId || undefined,
      startDate,
      quantity: subscription.quantity || 1,
      variantId: subscription.variantId || 0
    };
  }

  // ===== DATA LAYER =====

  /**
   * Get the user data for the data layer
   * @returns DataLayer_SignUpFunnel_User
   */
  private getUserDataLayer(): DataLayer_SignUpFunnel_User {
    return {
      email: this.addressForm.get('email')?.value!,
      street: this.deliveryAddress()?.street || '',
      city: this.addressForm.get('city')?.value!,
      state: this.addressForm.get('state')?.value!,
      postal_code: this.addressForm.get('zip')?.value!
    }
  }


}
